import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ImportAreaStatus,
  ImportDisplayStatus,
  ProductImportReportStatus,
} from '../../service-components/products/import-reports/product-import-report-types';
import { toDisplayStatus } from '../../service-components/products/import-reports/product-import-report-status.util';

// localStorage key remembering whether the user folded the system view away.
export const IMPORT_STAGES_COLLAPSED_KEY = 'importStagesCollapsed';

// One factory projected onto the 5-column / 2-row plane of the system view.
interface DiagramStage {
  label: string;
  col: number;
  row: number;
  // Only CSVToDatabase is data-driven; every other factory is grey decoration.
  live: boolean;
}

// A directed connector between two factories, addressed by grid position.
interface DiagramEdge {
  fromCol: number;
  fromRow: number;
  toCol: number;
  toRow: number;
}

// A static "shape of the system" diagram for the top of the Product import status
// page. Seven factories on two lanes; only the CSVToDatabase factory takes colour,
// reflecting the aggregate of the import-area board already loaded by the parent.
// The whole diagram can be folded away; that choice is remembered in localStorage.
@Component({
  selector: 'app-import-stages-diagram',
  templateUrl: './import-stages-diagram.component.html',
  styleUrls: ['./import-stages-diagram.component.scss'],
})
export class ImportStagesDiagramComponent implements OnInit, OnDestroy {
  @Input() areas: ImportAreaStatus[] = [];

  // Expanded by default; only a stored preference collapses it.
  collapsed = false;

  // Pixel size of the diagram band. The connector SVG draws in these coordinates
  // (no viewBox, so 1 user unit = 1px) which keeps the arrowheads perfectly
  // proportioned at any width instead of skewing with a stretched viewBox.
  viewW = 1000;
  viewH = 150;

  private bandElement?: HTMLElement;
  private resizeObserver?: ResizeObserver;

  // The band is behind an *ngIf (it is removed when folded), so a setter re-attaches
  // the ResizeObserver every time it reappears.
  @ViewChild('band') set band(ref: ElementRef<HTMLElement> | undefined) {
    this.bandElement = ref?.nativeElement;
    this.attachResizeObserver();
  }

  readonly stages: DiagramStage[] = [
    { label: 'Productsup', col: 1, row: 1, live: false },
    { label: 'CSVToDatabase', col: 2, row: 1, live: true },
    { label: 'FestoATPInfoParser', col: 2, row: 2, live: false },
    { label: 'ProductDB', col: 3, row: 1, live: false },
    { label: 'DatabaseToShop', col: 4, row: 1, live: false },
    { label: 'ICM', col: 5, row: 1, live: false },
    { label: 'IOM', col: 5, row: 2, live: false },
  ];

  // Source -> target for every connector; the arrow is drawn pointing at the target.
  readonly edges: DiagramEdge[] = [
    { fromCol: 1, fromRow: 1, toCol: 2, toRow: 1 }, // Productsup -> CSVToDatabase
    { fromCol: 2, fromRow: 1, toCol: 3, toRow: 1 }, // CSVToDatabase -> ProductDB
    { fromCol: 2, fromRow: 2, toCol: 3, toRow: 1 }, // FestoATPInfoParser -> ProductDB
    { fromCol: 2, fromRow: 2, toCol: 5, toRow: 2 }, // FestoATPInfoParser -> IOM
    { fromCol: 3, fromRow: 1, toCol: 4, toRow: 1 }, // ProductDB -> DatabaseToShop
    { fromCol: 4, fromRow: 1, toCol: 5, toRow: 1 }, // DatabaseToShop -> ICM
    { fromCol: 4, fromRow: 1, toCol: 5, toRow: 2 }, // DatabaseToShop -> IOM
  ];

  ngOnInit(): void {
    this.collapsed = localStorage.getItem(IMPORT_STAGES_COLLAPSED_KEY) === '1';
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem(
      IMPORT_STAGES_COLLAPSED_KEY,
      this.collapsed ? '1' : '0',
    );
  }

  // Connector geometry in band pixels, including each arrowhead's midpoint and angle
  // so the arrow can be placed in the gap between factories, pointing along the flow.
  get connectors(): {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    midX: number;
    midY: number;
    angle: number;
  }[] {
    return this.edges.map(edge => {
      const x1 = this.columnX(edge.fromCol);
      const y1 = this.laneY(edge.fromRow);
      const x2 = this.columnX(edge.toCol);
      const y2 = this.laneY(edge.toRow);
      return {
        x1,
        y1,
        x2,
        y2,
        midX: (x1 + x2) / 2,
        midY: (y1 + y2) / 2,
        angle: (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI,
      };
    });
  }

  // Collapse every area's latest display state into the single CSVToDatabase colour:
  // a failure wins, then a running import, then warnings, then all-successful, otherwise
  // nothing has run yet (grey). Mirrors the precedence the board itself implies; a single
  // never-run area still drops the aggregate to grey.
  get csvToDatabaseStatus(): ImportDisplayStatus {
    const statuses = this.areas.map(area =>
      toDisplayStatus(area.latestStatus, area.counts),
    );
    if (
      statuses.some(
        status => status === ProductImportReportStatus.TECHNICAL_FAILURE,
      )
    ) {
      return ProductImportReportStatus.TECHNICAL_FAILURE;
    }
    if (
      statuses.some(
        status => status === ProductImportReportStatus.FUNCTIONAL_FAILURE,
      )
    ) {
      return ProductImportReportStatus.FUNCTIONAL_FAILURE;
    }
    if (statuses.some(status => status === ProductImportReportStatus.RUNNING)) {
      return ProductImportReportStatus.RUNNING;
    }
    // Remaining areas are successful, success-with-warnings, or never-run: orange when
    // every run succeeded and at least one carried warnings, green when all clean.
    if (
      statuses.length > 0 &&
      statuses.every(
        status =>
          status === ProductImportReportStatus.SUCCESSFUL ||
          status === 'WARNING',
      )
    ) {
      return statuses.some(status => status === 'WARNING')
        ? 'WARNING'
        : ProductImportReportStatus.SUCCESSFUL;
    }
    return null;
  }

  get isCsvRunning(): boolean {
    return this.csvToDatabaseStatus === ProductImportReportStatus.RUNNING;
  }

  // Colour class for a factory's icon. Grey decoration factories share `stage--muted`;
  // the live factory borrows Bootstrap's text utilities so it matches the board badges.
  stageIconClass(stage: DiagramStage): string {
    if (!stage.live) {
      return 'stage--muted';
    }
    switch (this.csvToDatabaseStatus) {
      case ProductImportReportStatus.SUCCESSFUL:
        return 'text-success';
      case 'WARNING':
        return 'text-warnings';
      case ProductImportReportStatus.FUNCTIONAL_FAILURE:
      case ProductImportReportStatus.TECHNICAL_FAILURE:
        return 'text-danger';
      case ProductImportReportStatus.RUNNING:
        return 'text-warning';
      default:
        return 'stage--muted';
    }
  }

  private attachResizeObserver(): void {
    this.resizeObserver?.disconnect();
    const element = this.bandElement;
    if (element && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measure(element));
      this.resizeObserver.observe(element);
    }
  }

  private measure(element: HTMLElement): void {
    this.viewW = element.clientWidth || this.viewW;
    this.viewH = element.clientHeight || this.viewH;
  }

  // Five columns; centres at 10/30/50/70/90 % of the width.
  private columnX(col: number): number {
    return ((col * 2 - 1) / 10) * this.viewW;
  }

  // Two lanes, biased towards the icon row rather than the cell centre.
  private laneY(row: number): number {
    return (row === 1 ? 0.3 : 0.72) * this.viewH;
  }
}
